import { useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import { Link as StyledLink, Avatar } from '~/elements'
import { NEW_BOT_NAME } from '~/stores'
import { type Bot } from '../hooks/useBotSort'
import { IntegrationIcon } from '~/features/integrations/components'
import { BotIcon } from '~/components/BotIcon'
import { getUseConfigStore } from '~/features/webchat/stores/useConfigStore'
import { BotCardDropdown } from './BotCardDropdown'
import { MessagesAnalyticsWidget } from './MessagesAnalyticsWidget'
import { ErrorsAnalyticsWidget } from './ErrorsAnalyticsWidget'
import { Boundary } from '~/components'
import { trackEvent } from '../../../providers'
import { Edit, MoreVertical } from 'lucide-react'
import { Button, Card, CardContent, Tooltip, TooltipContent, TooltipTrigger } from '@bpinternal/ui-kit-next'
import { Link } from '@tanstack/react-router'
import { getCdmStudioUrl } from '~/shared/configurations'
import { AlwaysAliveBadge } from './AlwaysAliveBadge'
import { useSuspenseQuery as useTanstackSuspenseQuery } from '@tanstack/react-query'
import { useIntegrations } from '~/hooks/integrations/useIntegrations'
import { botQueryOptions } from '~/queries'
import { useSuspenseQuery } from '~/services'

type BotListViewProps = {
  bots: Bot[]
  workspaceId: string
  userId: string
}

export const BotListView = ({ bots, workspaceId, userId }: BotListViewProps) => {
  return (
    <>
      {bots.length > 0 && (
        <Card className="cursor-pointer">
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {bots.map((bot) => (
                <BotListRow key={bot.id} bot={bot} workspaceId={workspaceId} userId={userId} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  )
}

type BotListRowProps = {
  bot: Bot
  workspaceId: string
  userId: string
}

const BotListRow = ({ bot, workspaceId, userId }: BotListRowProps) => {
  const [alwaysAlivePending, setAlwaysAlivePending] = useState(false)
  const botDetailed = useTanstackSuspenseQuery(botQueryOptions({ botId: bot.id, workspaceId })).data
  const { data: bpBot } = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_', {
    botId: bot.id,
    workspaceId,
  })

  const botIntegrations = useIntegrations({
    workspaceId,
    integrationIds: Object.values(botDetailed.integrations).map((i) => i.id),
  })

  const communicationChannelIntegrations = botIntegrations.filter((integration) => {
    return integration?.interfaces['typing-indicator'] !== undefined
  })

  const useConfigStore = useMemo(() => getUseConfigStore({ botId: bot.id, workspaceId }), [bot.id, workspaceId])
  const botAvatar = useConfigStore((state) => state.botAvatar)

  const openInStudio = () => {
    window.open(getCdmStudioUrl(bot.id))
  }

  return (
    <Link
      preload={false}
      to="/workspaces/$workspaceId/bots/$botId"
      params={{ workspaceId, botId: bot.id }}
      className="flex cursor-pointer items-center justify-between p-4 transition-colors hover:bg-muted/50"
      onClick={(e) => {
        const target = e.target as HTMLElement
        const isInteractiveElement = target.closest('button, [role="button"], .interactive')
        if (isInteractiveElement) {
          e.preventDefault()
        }
      }}
    >
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <div className="flex-shrink-0">
          {botAvatar ? (
            <Avatar name={bot.name} pictureUrl={botAvatar} className="h-10 w-10 rounded-full" variant="solid" />
          ) : (
            <BotIcon size="4" id={bot.id} className="flex-none" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <StyledLink
              className="truncate text-base font-semibold"
              to="/workspaces/$workspaceId/bots/$botId"
              params={{ workspaceId, botId: bot.id }}
              onClick={(e) => {
                e.stopPropagation()
                trackEvent({ type: 'navigate_to_bot', from: 'all_bots' })
              }}
              preload={false}
            >
              {bot.name || NEW_BOT_NAME}
            </StyledLink>
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>
              {bot?.deployedAt ? `Deployed ${DateTime.fromISO(bot?.deployedAt).toRelative()} on` : 'Not deployed'}
            </span>
            {bot?.deployedAt && communicationChannelIntegrations.length > 0 && (
              <div className="flex items-center gap-1">
                {communicationChannelIntegrations
                  .slice(0, 2)
                  .map(
                    (integration) =>
                      integration && (
                        <IntegrationIcon key={integration.id} iconUrl={integration.iconUrl} className="h-4 w-4" />
                      )
                  )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-shrink-0 items-center gap-4">
        <div className="hidden xl:block">
          <Boundary suspenseFallback={null} onError={() => null}>
            <div className="flex gap-8">
              <div className="w-36">
                <MessagesAnalyticsWidget color={4} botId={bot.id} workspaceId={workspaceId} size="compact" />
              </div>
              <div className="w-36">
                <ErrorsAnalyticsWidget color={2} botId={bot.id} workspaceId={workspaceId} size="compact" />
              </div>
            </div>
          </Boundary>
        </div>

        <div className="flex-shrink-0">
          <div className="interactive flex items-center gap-1">
            <AlwaysAliveBadge alwaysAlive={bpBot?.alwaysAlive} pending={alwaysAlivePending} />
            <Tooltip>
              <TooltipContent>{'Edit in studio'}</TooltipContent>
              <TooltipTrigger asChild>
                <Button size="icon" variant="ghost" className="h-6 w-6 flex-shrink-0" onClick={openInStudio}>
                  <Edit />
                </Button>
              </TooltipTrigger>
            </Tooltip>
            <BotCardDropdown
              botId={bot.id}
              workspaceId={workspaceId}
              userId={userId}
              botName={bot.name || NEW_BOT_NAME}
              setAlwaysAlivePending={setAlwaysAlivePending}
            >
              <button className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted">
                <MoreVertical className="h-4 w-4" />
              </button>
            </BotCardDropdown>
          </div>
        </div>
      </div>
    </Link>
  )
}
